import { useEffect, useMemo, useState } from "react";
import { FiDollarSign } from "react-icons/fi";
import { apiRequest } from "../../../services/api";

const number = (value) => Number(value || 0);
const money = (value) =>
  `₹${number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function existingRecovery(adjustments = []) {
  return adjustments.find(
    (adjustment) =>
      adjustment.type === "DEDUCTION" &&
      String(adjustment.description || "").startsWith("EMPLOYEE_ADVANCE:"),
  );
}

export default function PayrollAdvanceRecoveryCard({
  payrollItem,
  accessToken,
  onSaved,
}) {
  const [advances, setAdvances] = useState([]);
  const [advanceId, setAdvanceId] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const recovery = existingRecovery(payrollItem.adjustments);

  useEffect(() => {
    if (!accessToken || !payrollItem?.employeeId || recovery) return;

    let active = true;
    setLoading(true);
    apiRequest(`/employee-advances/employee/${payrollItem.employeeId}`, {
      accessToken,
    })
      .then((response) => {
        if (active) {
          setAdvances(
            (Array.isArray(response) ? response : response?.data || []).filter(
              (advance) => advance.status === "OPEN",
            ),
          );
        }
      })
      .catch((requestError) => {
        if (active)
          setError(requestError.message || "Unable to load advances.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [accessToken, payrollItem?.employeeId, recovery]);

  const selectedAdvance = useMemo(
    () => advances.find((advance) => advance.id === advanceId),
    [advanceId, advances],
  );
  const maximum = Math.min(
    number(payrollItem.netPayable),
    number(selectedAdvance?.outstandingAmount),
  );

  const save = async (event) => {
    event.preventDefault();
    if (!advanceId) return setError("Select an open advance first.");
    if (!amount || number(amount) <= 0 || number(amount) > maximum) {
      return setError(`Enter an amount up to ${money(maximum)}.`);
    }

    try {
      setSaving(true);
      setError("");
      await apiRequest(`/payroll/items/${payrollItem.id}/advance-recovery`, {
        method: "POST",
        accessToken,
        body: { employeeAdvanceId: advanceId, amount: number(amount) },
      });
      onSaved?.();
    } catch (requestError) {
      setError(requestError.message || "Unable to add advance recovery.");
    } finally {
      setSaving(false);
    }
  };

  if (payrollItem.status !== "DRAFT") return null;

  // A selected recovery is already visible as a payroll deduction. Do not
  // show the advance selector again for that employee's draft payroll item.
  if (recovery) return null;

  return (
    <section className="mt-5 rounded-2xl border border-primary-100 bg-primary-50/40 p-5">
      <div className="flex gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary-100 text-primary-800">
          <FiDollarSign />
        </span>
        <div>
          <h5 className="font-semibold text-gray-950">
            Recover an employee advance
          </h5>
          <p className="mt-1 text-sm leading-6 text-gray-600">
            Select one open advance to recover from this payroll. It becomes a
            repayment only when the run is posted.
          </p>
        </div>
      </div>
      {error ? (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading open advances…</p>
      ) : null}
      {!loading && !advances.length ? (
        <p className="mt-4 text-sm text-gray-500">
          This employee has no open advances.
        </p>
      ) : null}
      {advances.length ? (
        <form
          onSubmit={save}
          className="mt-4 grid gap-3 sm:grid-cols-[1fr_150px_auto] sm:items-end"
        >
          <label className="block text-sm font-semibold text-gray-800">
            <span className="mb-1.5 block">Open advance</span>
            <select
              value={advanceId}
              onChange={(event) => {
                setAdvanceId(event.target.value);
                setAmount("");
              }}
              className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
            >
              <option value="">Select an advance</option>
              {advances.map((advance) => (
                <option key={advance.id} value={advance.id}>
                  Issued{" "}
                  {new Date(advance.issuedOn).toLocaleDateString("en-IN")} ·{" "}
                  {money(advance.outstandingAmount)} outstanding
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-semibold text-gray-800">
            <span className="mb-1.5 block">Recovery amount</span>
            <input
              required
              min="0.01"
              max={maximum || undefined}
              step="0.01"
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={
                selectedAdvance ? `Max ${money(maximum)}` : "Select first"
              }
              disabled={!selectedAdvance}
              className="min-h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 disabled:bg-gray-100"
            />
          </label>
          <button
            disabled={!selectedAdvance || saving}
            className="min-h-11 rounded-xl bg-primary-800 px-4 text-sm font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Adding…" : "Add recovery"}
          </button>
        </form>
      ) : null}
      {selectedAdvance ? (
        <p className="mt-3 text-xs leading-5 text-gray-500">
          Maximum recovery is the lower of outstanding advance (
          {money(selectedAdvance.outstandingAmount)}) and current payroll payout
          ({money(payrollItem.netPayable)}).
        </p>
      ) : null}
    </section>
  );
}
